import { useCallback, useEffect, useMemo, useState } from 'react'
import type { CSSProperties } from 'react'

export const COMPOSITION_COLUMN_DEFAULTS: Record<string, number> = {
  tipo: 140,
  material: 220,
  acabamento: 130,
  consumo: 200,
  unidade: 150,
  custo: 130,
}

export const COMPOSITION_COLOR_COLUMN_DEFAULT = 170

const COMPOSITION_COLUMN_MIN: Record<string, number> = {
  tipo: 70,
  material: 120,
  acabamento: 70,
  consumo: 70,
  unidade: 70,
  custo: 70,
}

const COLOR_COLUMN_MIN = 100

const STORAGE_KEY = 'composition-table-column-widths'

export function colorColumnKey(colorId: number): string {
  return `color-${colorId}`
}

export function getColumnCssVar(columnKey: string): string {
  if (columnKey.startsWith('color-')) {
    const id = columnKey.replace('color-', '')
    return `--col-color-${id}`
  }
  return `--col-${columnKey}`
}

function getDefaultWidth(columnKey: string): number {
  if (columnKey.startsWith('color-')) {
    return COMPOSITION_COLOR_COLUMN_DEFAULT
  }
  return COMPOSITION_COLUMN_DEFAULTS[columnKey] ?? 100
}

function getMinWidth(columnKey: string): number {
  if (columnKey.startsWith('color-')) {
    return COLOR_COLUMN_MIN
  }
  return COMPOSITION_COLUMN_MIN[columnKey] ?? 70
}

function loadStoredWidths(): Record<string, number> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      if (parsed && typeof parsed === 'object') {
        return parsed as Record<string, number>
      }
    }
  } catch {
    // ignore invalid storage
  }
  return {}
}

export function useCompositionColumnResize(colorIds: number[]) {
  const [widths, setWidths] = useState<Record<string, number>>(loadStoredWidths)

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(widths))
    } catch {
      // ignore quota errors
    }
  }, [widths])

  const getWidth = useCallback(
    (columnKey: string) => {
      const stored = widths[columnKey]
      if (typeof stored === 'number' && stored > 0) {
        return stored
      }
      return getDefaultWidth(columnKey)
    },
    [widths],
  )

  const startResize = useCallback(
    (columnKey: string, clientX: number) => {
      const startWidth = getWidth(columnKey)
      const minWidth = getMinWidth(columnKey)

      const onMouseMove = (event: MouseEvent) => {
        const delta = event.clientX - clientX
        const nextWidth = Math.max(minWidth, startWidth + delta)
        setWidths((previous) => ({ ...previous, [columnKey]: nextWidth }))
      }

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }

      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    },
    [getWidth],
  )

  const columnWidthsStyle = useMemo(() => {
    const style: Record<string, string> = {
      '--col-tipo': `${getWidth('tipo')}px`,
      '--col-material': `${getWidth('material')}px`,
      '--col-acabamento': `${getWidth('acabamento')}px`,
      '--col-consumo': `${getWidth('consumo')}px`,
      '--col-unidade': `${getWidth('unidade')}px`,
      '--col-custo': `${getWidth('custo')}px`,
    }

    for (const colorId of colorIds) {
      const key = colorColumnKey(colorId)
      style[getColumnCssVar(key)] = `${getWidth(key)}px`
    }

    return style as CSSProperties
  }, [colorIds, getWidth])

  return { startResize, columnWidthsStyle, getWidth }
}
